"use client"
import { useRouter } from 'next/navigation';
import React, { createContext, useState, useEffect, useContext } from 'react';

export interface TeamUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'customer_success' | 'admin' | 'supervisor';
}

interface TeamAuthContextProps {
  teamUser: TeamUser | null;
  isLoadingTeamUser: boolean;
  signInWithEmail: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  signOut: () => Promise<void>;
  isTeamAuthenticating: boolean;
}

export const TeamAuthContext = createContext<TeamAuthContextProps>({
  teamUser: null,
  isLoadingTeamUser: true,
  signInWithEmail: async () => ({ success: false, message: '' }),
  signOut: async () => {},
  isTeamAuthenticating: false,
});

const HARDCODED_USERS = [
  {
    email: 'blake@fxnity.com',
    password: 'FXnity123!',
    id: '12345-blake-id',
    firstName: 'Blake',
    lastName: 'Admin',
    role: 'admin' as const
  }
];

export const TeamAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [teamUser, setTeamUser] = useState<TeamUser | null>(null);
  const [isLoadingTeamUser, setIsLoadingTeamUser] = useState(true);
  const [isTeamAuthenticating, setIsTeamAuthenticating] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const storedUser = localStorage.getItem('teamUser');
    if (storedUser) {
      setTeamUser(JSON.parse(storedUser));
    }
    setIsLoadingTeamUser(false);
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    setIsTeamAuthenticating(true);
    
    try {

      const lowerEmail = email.toLowerCase();
      const user = HARDCODED_USERS.find(u => u.email.toLowerCase() === lowerEmail && u.password === password);
      
      if (user) {
        const userData = {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role
        };
        
        console.log('User signed in:', userData);
        setTeamUser(userData);
        localStorage.setItem('teamUser', JSON.stringify(userData));
        
        router.push('/team-dashboard');
        
        return {
          success: true,
          message: 'Login successful',
        };
      } else {
        return {
          success: false,
          message: 'Invalid email or password',
        };
      }
    } catch (error) {
      console.error('Error signing in:', error);
      return {
        success: false,
        message: 'An unexpected error occurred. Please try again.',
      };
    } finally {
      setIsTeamAuthenticating(false);
    }
  };

  const signOut = async () => {
    setTeamUser(null);
    localStorage.removeItem('teamUser');
    navigate('/team-login');
  };

  return (
    <TeamAuthContext.Provider
      value={{
        teamUser,
        isLoadingTeamUser,
        signInWithEmail,
        signOut,
        isTeamAuthenticating,
      }}
    >
      {children}
    </TeamAuthContext.Provider>
  );
};

export const useTeamAuth = () => useContext(TeamAuthContext);
